import React, { useState } from 'react';
import Table, { CellProps, SortType } from 'rsuite/Table';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import Whisper from 'rsuite/Whisper';
import IconButton from 'rsuite/IconButton';
import { useNavigate } from 'react-router-dom';

import classNames from 'classnames';
import { CleanCustomsDeliverySupport } from '../../../../export-types/cleaned-types';
import { WhisperSpeaker } from '../../../../utils/whisper-speaker';
import { ToggleCell } from '../../../common/table/toggle-cell';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { trpc, RouterInput } from '../../../../api/trpc';
import { AlertService } from '../../../../services/alert.service';

const compareNumberValues = (
  a: CleanCustomsDeliverySupport,
  b: CleanCustomsDeliverySupport,
  field: keyof CleanCustomsDeliverySupport,
  sortType: SortType,
): number => {
  const aValueRaw = a?.[field];
  const bValueRaw = b?.[field];

  if (aValueRaw == null && bValueRaw == null) return 0;
  if (aValueRaw == null) return 1;
  if (bValueRaw == null) return -1;

  const aValue =
    typeof aValueRaw === 'string' ? parseFloat(aValueRaw) : aValueRaw;
  const bValue =
    typeof bValueRaw === 'string' ? parseFloat(bValueRaw) : bValueRaw;

  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return sortType === 'asc' ? aValue - bValue : bValue - aValue;
  }

  return 0;
};

export const CustomsDeliverySupportsTab = () => {
  const navigate = useNavigate();

  const input = {};
  const { data, mutate, isValidating } = useSWR(
    ['customsDeliverySupport.getList', input],
    () => trpc.customsDeliverySupport.getList.query(input),
  );
  const { trigger: updateMethod } = useSWRMutation(
    ['customsDeliverySupport.update'],
    (_k, { arg }: { arg: RouterInput['customsDeliverySupport']['update'] }) =>
      trpc.customsDeliverySupport.update.mutate(arg),
  );
  const [sortColumn, setSortColumn] = React.useState<string | undefined>();
  const [sortType, setSortType] = React.useState<SortType | undefined>();
  const [loading, setLoading] = useState<boolean>();

  const sortedAndFilteredData = React.useMemo(() => {
    let updateData = data || [];

    if (sortColumn && sortType && updateData.length > 0) {
      return [...updateData].sort((a, b) => {
        switch (sortColumn) {
          case 'name':
          case 'disabled':
          case 'period':
          case 'commissionRate':
            return compareNumberValues(
              a,
              b,
              sortColumn as keyof CleanCustomsDeliverySupport,
              sortType,
            );
          default:
            return 0;
        }
      });
    }

    updateData.sort((a, b) => {
      return a.period - b.period;
    });

    return updateData;
  }, [data, sortColumn, sortType]);

  const handleSortColumn = (
    dataKey: string,
    sortType?: SortType | undefined,
  ) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSortColumn(dataKey);
      setSortType(sortType);
    }, 500);
  };

  async function checkDeliverySupport(
    rowData: CleanCustomsDeliverySupport,
    value: boolean,
  ) {
    await updateMethod({
      id: rowData.id,
      data: { disabled: value },
    });
    await mutate();

    AlertService.success();
  }

  const PeriodCell = ({
    rowData,
    ...props
  }: CellProps<CleanCustomsDeliverySupport>) =>
    rowData && <Table.Cell {...props}>{rowData.period} дней</Table.Cell>;

  const renderMenu =
    (support: CleanCustomsDeliverySupport): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = (eventKey: number | string | undefined) => {
        onClose();
        if (eventKey === 1) {
          navigate(`/customs-deliveries/delivery-supports/create`, {
            state: support,
          });
        }
      };
      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey={1}>
              <i className="bi bi-pencil" />
              <span className="ps-2">Изменить</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={2}>
              <i className="bi bi-trash" />
              <span className="ps-2">Удалить</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  const ActionCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<CleanCustomsDeliverySupport>) => {
    return (
      rowData && (
        <Table.Cell
          {...props}
          className="link-group no-padding-cell pt-2"
          align="right"
        >
          <Whisper
            placement="leftStart"
            trigger="click"
            speaker={renderMenu(rowData)}
          >
            <IconButton
              appearance="subtle"
              icon={<i className="bi bi-three-dots" />}
            />
          </Whisper>
        </Table.Cell>
      )
    );
  };

  const DeliveryMethodNameCell = ({
    rowData,
    ...props
  }: CellProps<CleanCustomsDeliverySupport>) =>
    rowData && <Table.Cell {...props}>{rowData.name}</Table.Cell>;

  return (
    <>
      <Table
        data={sortedAndFilteredData}
        sortColumn={sortColumn}
        sortType={sortType}
        loading={
          (isValidating && sortedAndFilteredData.length === 0) || loading
        }
        headerHeight={57}
        autoHeight
        onSortColumn={handleSortColumn}
      >
        <Table.Column flexGrow={1} minWidth={100} fullText sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Название
          </Table.HeaderCell>
          <DeliveryMethodNameCell dataKey="name" />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100} fullText sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Период
          </Table.HeaderCell>
          <PeriodCell dataKey="period" />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100} sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Включен
          </Table.HeaderCell>
          <ToggleCell
            dataKey="disabled"
            inverted={true}
            onChange={checkDeliverySupport}
          />
        </Table.Column>
        <Table.Column>
          <Table.HeaderCell> </Table.HeaderCell>
          <ActionCell />
        </Table.Column>
      </Table>
    </>
  );
};
